export interface PageSeoConfig {
  titleEn: string;
  titleHr: string;
  descriptionEn: string;
  descriptionHr: string;
  canonicalPath: string;
  canonicalPathEn?: string;
  canonicalPathHr?: string;
  keywordsEn?: string[];
  keywordsHr?: string[];
  ogType?: "website" | "article" | "book";
  ogImage?: string;
  schemaType?: string;
  schemaData?: Record<string, any>;
}

export interface SubpageRouteConfig {
  id: number;
  slug: string;
  slugHr: string;
  key: string;
  aliases: string[];
}

export const SUBPAGE_ROUTES: SubpageRouteConfig[] = [
  {
    id: 1,
    slug: "/pleiades-mythology",
    slugHr: "/plejade-mitologija",
    key: "subpage1",
    aliases: ["/library/1", "/world-mythology", "/pleiades-in-world-mythology", "/mitologija-svijeta"]
  },
  {
    id: 2,
    slug: "/pleiadian-civilization",
    slugHr: "/plejadanska-civilizacija",
    key: "subpage2",
    aliases: ["/library/2", "/civilization-analysis", "/pleiades-civilization", "/civilizacija-plejada"]
  },
  {
    id: 3,
    slug: "/pleiadian-technology",
    slugHr: "/plejadanska-tehnologija",
    key: "subpage3",
    aliases: ["/library/3", "/technology-synthesis", "/crystal-technology", "/tehnologija-plejada"]
  },
  {
    id: 4,
    slug: "/pleiadians-human-history",
    slugHr: "/plejadani-ljudska-povijest",
    key: "subpage4",
    aliases: ["/library/4", "/human-history", "/starseed-history", "/ljudska-povijest"]
  },
  {
    id: 5,
    slug: "/comparative-cosmology",
    slugHr: "/komparativna-kozmologija",
    key: "subpage5",
    aliases: ["/library/5", "/cosmic-systems", "/comparative-systems", "/kozmicki-sustavi"]
  },
  {
    id: 6,
    slug: "/consciousness-evolution",
    slugHr: "/evolucija-svijesti",
    key: "subpage6",
    aliases: ["/library/6", "/evolution-of-consciousness", "/ascension-model", "/model-svijesti"]
  },
  {
    id: 7,
    slug: "/pleiadian-identity",
    slugHr: "/plejadanski-identitet",
    key: "subpage7",
    aliases: ["/library/7", "/identity-profile", "/pleiadian-identity-profile", "/zvjezdani-identitet"]
  },
  {
    id: 8,
    slug: "/lyra-orion-war",
    slugHr: "/lirsko-orionski-rat",
    key: "subpage8",
    aliases: ["/library/8", "/lyran-orion-war", "/battle-of-vega", "/rat-lyra-orion"]
  },
  {
    id: 9,
    slug: "/great-evacuation",
    slugHr: "/velika-evakuacija",
    key: "subpage9",
    aliases: ["/library/9", "/the-great-evacuation", "/cosmic-evacuation", "/cosmic-scattering", "/evakuacija-lyre"]
  },
  {
    id: 10,
    slug: "/fall-of-lyra",
    slugHr: "/pad-lyre",
    key: "subpage10",
    aliases: ["/library/10", "/the-fall-of-lyra", "/collapse-of-lyra", "/slom-lyre"]
  },
  {
    id: 11,
    slug: "/omega-black",
    slugHr: "/omega-black",
    key: "omegaBlack",
    aliases: ["/library/11", "/omega-vault", "/omega-documents", "/omega-black-documents"]
  },
  {
    id: 12,
    slug: "/genesis-roster",
    slugHr: "/genesis-roster",
    key: "genesisRoster",
    aliases: ["/library/12", "/genesis", "/roster", "/ark7-manifest"]
  },
  {
    id: 13,
    slug: "/books-of-fragments",
    slugHr: "/knjige-fragmenata",
    key: "booksOfFragments",
    aliases: ["/library/13", "/fragments", "/fragment-books"]
  }
];

export function getSubpagePath(subpageId: number, isEn: boolean = true): string {
  const route = SUBPAGE_ROUTES.find(r => r.id === subpageId);
  if (!route) return `/library/${subpageId}`;
  return isEn ? route.slug : route.slugHr;
}

export function getSubpageIdFromPath(path: string): number | null {
  const cleanPath = path.toLowerCase().replace(/\/$/, "");
  for (const route of SUBPAGE_ROUTES) {
    if (route.slug === cleanPath || route.slugHr === cleanPath) return route.id;
    if (route.aliases.includes(cleanPath)) return route.id;
  }
  return null;
}

export const SUBPAGE_SEO_CONFIGS: Record<string, PageSeoConfig> = {
  home: {
    titleEn: "Pleiades M45 • Celestial Intelligence & Sacred Geometry",
    titleHr: "Plejade M45 • Nebeska Inteligencija i Sveta Geometrija",
    descriptionEn: "Explore Pleiades M45 — sacred geometry, Pleiadian archetypes, cosmic history of Lyra and Orion, frequency physics, Solfeggio sound tuning, and live star telemetry.",
    descriptionHr: "Istražite Plejade M45 — svetu geometriju, plejadanske arhetipove, kozmičku povijest Lyre i Oriona, frekvencijsku fiziku i živu zvjezdanu telemetriju.",
    canonicalPath: "/",
    keywordsEn: ["Pleiades", "M45", "Seven Sisters", "Sacred Geometry", "Lyran Genesis", "Fall of Lyra", "Cosmic Exodus", "Solfeggio 432Hz"],
    keywordsHr: ["Plejade", "M45", "Sedam Sestara", "Sveta Geometrija", "Pad Lyre", "Kozmički Egzodus", "Solfeggio frekvencije"],
    ogType: "website"
  },
  pleiadesLibrary: {
    titleEn: "Pleiadian Galactic Library • Lyran Archival Sanctuary (268,000 BCE)",
    titleHr: "Plejadanska Galaktička Knjižnica • Arhivsko Svetište (268.000 pr. Kr.)",
    descriptionEn: "The Great Pleiadian Galactic Library: Decrypted fragments of the Lyran archive preserved across 268,000 years. Access Books of Fragments, Genesis Roster, and Omega Black vault.",
    descriptionHr: "Velika Plejadanska Galaktička Knjižnica: Dešifrirani fragmenti Lirskog arhiva očuvani kroz 268.000 godina. Pristupite Knjigama Fragmenata, Genesis Rosteru i Omega Black dosjeima.",
    canonicalPath: "/pleiades-library",
    keywordsEn: ["Pleiadian Library", "Galactic Archive", "Lyran Archive", "Books of Fragments", "Omega Black", "Genesis Roster", "M45 Sanctuary", "268000 BCE"],
    keywordsHr: ["Plejadanska knjižnica", "Galaktički arhiv", "Lirski arhiv", "Knjige fragmenata", "Omega Black", "Genesis Roster", "M45 arhiv"],
    ogType: "website",
    schemaType: "CollectionPage"
  },
  genesisRoster: {
    titleEn: "Genesis Roster (ARK-7 Manifest) • 79,999 Recorded Souls • Pleiades M45",
    titleHr: "Genesis Roster (Manifest ARK-7) • 79.999 Zabilježenih Duša • Plejade M45",
    descriptionEn: "Official passenger manifest of the ARK-7 Ark of Memory. 79,999 consciousnesses across 4 Keeper classes, the missing 80,000th soul, and the 431.997 Hz carrier vector.",
    descriptionHr: "Službeni manifest putnika broda ARK-7 (Brod Pamćenja). 79.999 svjesti u 4 klase čuvara, zagonetka 80.000-e duše i nosivi frekventni vektor 431,997 Hz.",
    canonicalPath: "/genesis-roster",
    keywordsEn: ["Genesis Roster", "ARK-7 Manifest", "80000 Souls", "431.997 Hz", "Navigator Petrov", "Crystal Chroniclers", "Genetic Weavers", "Resonance Masters"],
    keywordsHr: ["Genesis Roster", "Manifest ARK-7", "80000 duša", "431.997 Hz", "Navigator Petrov", "Kristalni kroničari", "Genetski tkalci", "Majstori rezonancije"],
    ogType: "article",
    schemaType: "Dataset",
    schemaData: {
      "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/",
      "creator": {
        "@type": "Person",
        "name": "NT",
        "url": "https://pleiades-m45.com"
      }
    }
  },
  booksOfFragments: {
    titleEn: "Books of Fragments • 10 Sacred Codices of the Lyran Archive • Pleiades M45",
    titleHr: "Knjige Fragmenata • 10 Svetih Kodeksa Lirskog Arhiva • Plejade M45",
    descriptionEn: "Complete translated codices from the Alcyone crystal strata. From the Book of the First Separation to Atlantis, the Tuaoi crystal grid, and the cosmic dispersal.",
    descriptionHr: "Cjeloviti prevedeni kodeksi iz kristalnih slojeva Alcyone. Od Knjige Prvog Razdvajanja do Atlantide, Tuaoi kristalne rešetke i kozmičkog raspršenja.",
    canonicalPath: "/books-of-fragments",
    canonicalPathEn: "/books-of-fragments",
    canonicalPathHr: "/knjige-fragmenata",
    keywordsEn: ["Books of Fragments", "First Separation", "Atlantis Frequency", "Tuaoi Crystal", "Alcyone Spire", "Sacred Codices", "Pleiadian Texts"],
    keywordsHr: ["Knjige fragmenata", "Prvo razdvajanje", "Atlantida", "Tuaoi kristal", "Alcyone", "Sveti kodeksi", "Plejadanski tekstovi"],
    ogType: "book",
    schemaType: "BookSeries"
  },
  omegaBlack: {
    titleEn: "Omega Black Documents • Level 4 Restricted Dossiers • Pleiades M45",
    titleHr: "Omega Black Dokumenti • Povjerljivi Dosjei Razine 4 • Plejade M45",
    descriptionEn: "Declassified Omega Black intelligence dossiers: Sol-3 Quarantine Protocol CN_447, ARK-7 navigation room transcripts, Council of Nine accords, and Orion warfare analysis.",
    descriptionHr: "Dešifrirani dosjei Omega Black: Sol-3 Protokol Karantene CN_447, transkripti navigacijske sobe broda ARK-7, zapisi Vijeća Devetorice i analiza sukoba s Orionom.",
    canonicalPath: "/omega-black",
    keywordsEn: ["Omega Black", "Sol-3 Quarantine", "Protocol CN_447", "Council of Nine", "Orion War", "ARK-7 Anomaly", "Declassified Galactic Dossiers"],
    keywordsHr: ["Omega Black", "Sol-3 karantena", "Protokol CN_447", "Vijeće Devetorice", "Rat s Orionom", "ARK-7 anomalija", "Povjerljivi dosjei"],
    ogType: "article",
    schemaType: "TechArticle"
  },
  lyraChronicles: {
    titleEn: "Lyra Chronicles • Reconstruction of a Forgotten Civilization • Pleiades M45",
    titleHr: "Kronike Lyre • Rekonstrukcija Zaboravljene Civilizacije • Plejade M45",
    descriptionEn: "Enter the Lyra Chronicles: The ancient interstellar archive. Vega & Elyra, the Orion homeworld, the Great Archive, Two Worlds philosophy, and the surviving crystal fragments.",
    descriptionHr: "Kronike Lyre: Uđite u drevni međuzvjezdani arhiv. Vega i Elyra, svijet Oriona, Veliki arhiv Lyre, filozofija Dva Svijeta i preživjeli kristalni fragmenti.",
    canonicalPath: "/lyra-chronicles",
    canonicalPathEn: "/lyra-chronicles",
    canonicalPathHr: "/kronike-lyre",
    keywordsEn: ["Lyra Chronicles", "Kronike Lyre", "Vega", "Elyra", "Orion", "Great Archive of Lyra", "Ancient Interstellar Archive", "Two Worlds", "7 Arks", "Pleiades M45"],
    keywordsHr: ["Kronike Lyre", "Lyra Chronicles", "Vega", "Elyra", "Orion", "Veliki arhiv Lyre", "Dva svijeta", "7 arki", "Međuzvjezdani arhiv", "Plejade M45"],
    ogType: "article",
    schemaType: "CollectionPage"
  },
  subpage1: {
    titleEn: "Pleiades in World Mythology • Ancient Sacred Starlore • Pleiades M45",
    titleHr: "Plejade u Mitologiji Svijeta • Drevna Zvjezdana Predaja • Plejade M45",
    descriptionEn: "Explore the Pleiades (M45) across ancient terrestrial cultures: Greek Seven Sisters of Atlas, Mayan Tzab-ek calendar cycles, Lakota celestial lore, and Egyptian cosmologies.",
    descriptionHr: "Istražite Plejade (M45) kroz drevne svjetske kulture: grčkih Sedam Sestara, majanske kalendarske cikluse Tzab-ek, mitove Lakota Indijanaca i egipatsku kozmologiju.",
    canonicalPath: "/pleiades-mythology",
    canonicalPathEn: "/pleiades-mythology",
    canonicalPathHr: "/plejade-mitologija",
    keywordsEn: ["Pleiades Mythology", "World Mythology", "Seven Sisters", "Atlas Daughters", "Mayan Astronomy", "Egyptian Cosmology", "Pleiades M45"],
    keywordsHr: ["Mitologija Plejada", "Mitologija svijeta", "Sedam sestara", "Kćeri Atlasa", "Majanska astronomija", "Egipatska kozmologija", "Plejade M45"],
    ogType: "article"
  },
  subpage2: {
    titleEn: "Pleiadian Civilization — Deep Analysis & Society • Pleiades M45",
    titleHr: "Plejadanska Civilizacija — Dubinska Analiza i Društvo • Plejade M45",
    descriptionEn: "Comprehensive study of Pleiadian society on Erra and Taygeta: harmonic social architecture, 5D governance, bio-resonant cities, and educational paradigms.",
    descriptionHr: "Dubinska analiza plejadanskog društva na planetu Erra (Taygeta): harmonijska društvena arhitektura, 5D uprava, bio-rezonantni gradovi i modeli obrazovanja.",
    canonicalPath: "/pleiadian-civilization",
    canonicalPathEn: "/pleiadian-civilization",
    canonicalPathHr: "/plejadanska-civilizacija",
    keywordsEn: ["Pleiadian Civilization", "Planet Erra", "Taygeta Society", "5D Culture", "Harmonic Architecture", "Pleiades M45"],
    keywordsHr: ["Plejadanska civilizacija", "Planet Erra", "Društvo Taygete", "5D kultura", "Harmonijska arhitektura", "Plejade M45"],
    ogType: "article"
  },
  subpage3: {
    titleEn: "Pleiadian Technology — Scientific-Metaphysical Synthesis • Pleiades M45",
    titleHr: "Tehnologija Plejada — Znanstveno-Metafizička Sinteza • Plejade M45",
    descriptionEn: "Pleiadian advanced science: crystalline amplifiers, bioresonant organic craft, tachyon propulsion fields, zero-point energy generators, and consciousness navigation.",
    descriptionHr: "Napredna plejadanska znanost: kristalni pojačivači, bio-rezonantni organski brodovi, tahionski pogon, generatori nulte točke i navigacija sviješću.",
    canonicalPath: "/pleiadian-technology",
    canonicalPathEn: "/pleiadian-technology",
    canonicalPathHr: "/plejadanska-tehnologija",
    keywordsEn: ["Pleiadian Technology", "Crystalline Amplifiers", "Tachyon Propulsion", "Bioresonant Craft", "Zero-Point Energy", "Pleiades M45"],
    keywordsHr: ["Tehnologija Plejada", "Kristalni pojačivači", "Tahionski pogon", "Bio-rezonantni brodovi", "Energija nulte točke", "Plejade M45"],
    ogType: "article"
  },
  subpage4: {
    titleEn: "Pleiadians and Human History • Ancient Contacts & Heritage • Pleiades M45",
    titleHr: "Plejadani i Ljudska Povijest • Drevni Kontakti i Nasljeđe • Plejade M45",
    descriptionEn: "The historical interaction between Pleiadians and humanity: starseed genetics, ancient megalithic alignments, Sumerian and Egyptian contact, and the 12-strand DNA template.",
    descriptionHr: "Povijesna interakcija Plejadana i čovječanstva: starseed genetika, drevna megalitska poravnanja, kontakti u Sumeru i Egiptu te 12-struki DNK obrazac.",
    canonicalPath: "/pleiadians-human-history",
    canonicalPathEn: "/pleiadians-human-history",
    canonicalPathHr: "/plejadani-ljudska-povijest",
    keywordsEn: ["Pleiadians Human History", "Starseed Heritage", "Ancient Astronauts", "Megalithic Alignments", "Human Genetics", "Pleiades M45"],
    keywordsHr: ["Plejadani ljudska povijest", "Starseed nasljeđe", "Drevni kontakti", "Megalitska poravnanja", "Ljudska genetika", "Plejade M45"],
    ogType: "article"
  },
  subpage5: {
    titleEn: "Comparative Cosmology — Sirius, Orion & Pleiades • Pleiades M45",
    titleHr: "Komparativna Kozmologija — Sirius, Orion i Plejade • Plejade M45",
    descriptionEn: "Comparative analysis of major star systems: Sirius B geometric masters, Orion dual polarity, Andromeda multidimensional observers, and the Pleiadian heart frequency.",
    descriptionHr: "Usporedna analiza ključnih zvjezdanih sustava: Sirijus B i geometrija, Orion i dualna polarnost, Andromeda i promatrači, te plejadanska frekvencija srca.",
    canonicalPath: "/comparative-cosmology",
    canonicalPathEn: "/comparative-cosmology",
    canonicalPathHr: "/komparativna-kozmologija",
    keywordsEn: ["Comparative Cosmology", "Sirius vs Pleiades", "Orion System", "Andromeda Observers", "Star Systems", "Pleiades M45"],
    keywordsHr: ["Komparativna kozmologija", "Sirijus i Plejade", "Orion sustav", "Andromeda", "Zvjezdani sustavi", "Plejade M45"],
    ogType: "article"
  },
  subpage6: {
    titleEn: "Evolution of Consciousness — Pleiadian Ascension Model • Pleiades M45",
    titleHr: "Evolucija Svijesti — Plejadanski Model Uzašašća • Plejade M45",
    descriptionEn: "The 7-density evolutionary framework: from 1D elemental awareness through 3D ego-polarity and 5D unity consciousness to 7D non-dual source immersion.",
    descriptionHr: "Evolucijski okvir kroz 7 gustoća svijesti: od 1D elementarne svijesti preko 3D ego-polariteta i 5D jedinstva do 7D nedualnog izvora.",
    canonicalPath: "/consciousness-evolution",
    canonicalPathEn: "/consciousness-evolution",
    canonicalPathHr: "/evolucija-svijesti",
    keywordsEn: ["Evolution of Consciousness", "Consciousness Evolution", "5D Ascension", "Density Models", "Spiritual Awakening", "Pleiades M45"],
    keywordsHr: ["Evolucija svijesti", "Uzašašće 5D", "Gustoće svijesti", "Duhovno buđenje", "Plejadanski model", "Plejade M45"],
    ogType: "article"
  },
  subpage7: {
    titleEn: "Pleiadian Identity Profile: Stellar Origin & Cosmic Essence • Pleiades M45",
    titleHr: "Plejadanski Identitet: Zvjezdano Podrijetlo i Kozmička Bit • Plejade M45",
    descriptionEn: "Complete telemetric readout of the Pleiadian Identity Profile: physical astronomy of M45, mythological memory of the daughters of Atlas, and soul origin resonance.",
    descriptionHr: "Cjeloviti telemetrijski profil plejadanskog identiteta: fizička astronomija skupa M45, mitološko sjećanje kćeri Atlasa i rezonancija podrijetla duše.",
    canonicalPath: "/pleiadian-identity",
    canonicalPathEn: "/pleiadian-identity",
    canonicalPathHr: "/plejadanski-identitet",
    keywordsEn: ["Pleiadian Identity", "Identity Profile", "Stellar Origin", "Starseed Identity", "Atlas Daughters", "M45 Telemetry", "Pleiades M45"],
    keywordsHr: ["Plejadanski identitet", "Profil identiteta", "Zvjezdano podrijetlo", "Starseed identitet", "Kćeri Atlasa", "M45 telemetrija", "Plejade M45"],
    ogType: "article"
  },
  subpage8: {
    titleEn: "Lyran–Orion War • The Great Cosmic Schism & Battle of Vega • Pleiades M45",
    titleHr: "Lirsko–Orionski Rat • Veliki Kozmički Rascjep i Bitka za Vegu • Plejade M45",
    descriptionEn: "Historical records of the Lyran-Orion War: ideological divergence between the Harmonic Federation and the Orion Empire, the orbital bombardment of Vega, and the cosmic turning point.",
    descriptionHr: "Povijesni zapisi Lirsko-orionskog rata: ideološki rascjep Harmonijske Federacije i Carstva Oriona, bombardiranje orbite Vege i prekretnica galaktičke povijesti.",
    canonicalPath: "/lyra-orion-war",
    canonicalPathEn: "/lyra-orion-war",
    canonicalPathHr: "/lirsko-orionski-rat",
    keywordsEn: ["Lyra Orion War", "Lyran Orion War", "Battle of Vega", "Orion Empire", "Cosmic Schism", "Vega Cataclysm", "Great War", "Pleiades M45"],
    keywordsHr: ["Lirsko orionski rat", "Rat Lyra Orion", "Bitka za Vegu", "Carstvo Oriona", "Kozmički rascjep", "Kataklizma Vege", "Plejade M45"],
    ogType: "article"
  },
  subpage9: {
    titleEn: "The Great Evacuation & Cosmic Scattering • Flight of the 7 Arks • Pleiades M45",
    titleHr: "Velika Evakuacija i Kozmičko Rasipanje • Putanje 7 Arki • Plejade M45",
    descriptionEn: "Flight vectors and interstellar dispersion following the fall of Vega: the launch of the Seven Arks, diaspora through the Perseus arm to Taygeta, Alcyone, and the sanctuary of Sol-3.",
    descriptionHr: "Vektori leta i međuzvjezdano rasipanje nakon pada Vege: lansiranje Sedam Arki, dijaspora kroz krak Perzeja prema Taygeti, Alcyoni i utočištu na Sol-3.",
    canonicalPath: "/great-evacuation",
    canonicalPathEn: "/great-evacuation",
    canonicalPathHr: "/velika-evakuacija",
    keywordsEn: ["The Great Evacuation", "Great Evacuation", "Cosmic Scattering", "Seven Arks", "ARK-7", "Flight Vectors", "Exodus of Lyra", "Pleiades M45"],
    keywordsHr: ["Velika evakuacija", "Kozmičko rasipanje", "Sedam arki", "ARK-7", "Vektori leta", "Egzodus Lyre", "Plejade M45"],
    ogType: "article"
  },
  subpage10: {
    titleEn: "Fall of Lyra — Collapse of a High Interstellar Civilization • Pleiades M45",
    titleHr: "Pad Lyre — Slom Visoke Međuzvjezdane Civilizacije • Plejade M45",
    descriptionEn: "Deep historical chronicle of the Fall of Lyra: the tragic unraveling of a civilization consecrated to beauty, science, and freedom, and the lessons of forgotten peace.",
    descriptionHr: "Duboka povijesna kronika Pada Lyre: tragični slom civilizacije posvećene ljepoti, znanosti i slobodi, te bezvremenske pouke zaboravljenog sklada.",
    canonicalPath: "/fall-of-lyra",
    canonicalPathEn: "/fall-of-lyra",
    canonicalPathHr: "/pad-lyre",
    keywordsEn: ["Fall of Lyra", "Collapse of Lyra", "Ring Nebula Destruction", "Ancient Interstellar Empire", "Loss of Harmony", "Pleiades M45"],
    keywordsHr: ["Pad Lyre", "Slom Lyre", "Uništenje Lyre", "Drevno međuzvjezdano carstvo", "Gubitak sklada", "Plejade M45"],
    ogType: "article"
  },
  chronicles: {
    titleEn: "Lyra Chronicles • Reconstruction of a Forgotten Civilization • Pleiades M45",
    titleHr: "Kronike Lyre • Rekonstrukcija Zaboravljene Civilizacije • Plejade M45",
    descriptionEn: "Enter the Lyra Chronicles: The digital reconstruction of an ancient forgotten interstellar civilization. Explore the Vega/Elyra and Orion homeworlds, the Great Archive, and the 7 Arks.",
    descriptionHr: "Uđite u Kronike Lyre: Digitalna rekonstrukcija drevne zaboravljene međuzvjezdane civilizacije. Istražite svjetove Vega/Elyra i Orion, Veliki arhiv i 7 Arki.",
    canonicalPath: "/lyra-chronicles",
    canonicalPathEn: "/lyra-chronicles",
    canonicalPathHr: "/kronike-lyre",
    keywordsEn: ["Lyra Chronicles", "Kronike Lyre", "Vega", "Elyra", "Orion Archonate", "Great Archive of Lyra", "Ancient Interstellar Archive", "Two Worlds", "7 Arks", "Pleiades M45"],
    keywordsHr: ["Kronike Lyre", "Lyra Chronicles", "Vega", "Elira", "Orionski Arhont", "Veliki arhiv Lyre", "Dva svijeta", "7 arki", "Međuzvjezdani arhiv", "Plejade M45"],
    ogType: "article",
    schemaType: "CollectionPage",
    schemaData: {
      "headline": "Lyra Chronicles — Reconstruction of a Forgotten Civilization",
      "mainEntityOfPage": "https://pleiades-m45.com/lyra-chronicles",
      "about": [
        { "@type": "Thing", "name": "Vega (Alpha Lyrae)" },
        { "@type": "Thing", "name": "Elyra Homeworld" },
        { "@type": "Thing", "name": "The Great Archive of Lyra" },
        { "@type": "Thing", "name": "Orion Archonate" },
        { "@type": "Thing", "name": "Seven Arks of Memory" }
      ]
    }
  },
  lyraPrologue: {
    titleEn: "What Are the Chronicles of Lyra, Really? • Prologue Codex • Pleiades M45",
    titleHr: "Što su zapravo Kronike Lyre? • Prološki Kodeks • Plejade M45",
    descriptionEn: "What are the Chronicles of Lyra, really? Before chronicles, memories. Before wars, questions. Declassified discovery fragments, the Orion schism, and the mystery of the Field.",
    descriptionHr: "Što su zapravo Kronike Lyre? Prije kronika sjećanja, prije ratova pitanja. Deklasificirani fragmenti otkrića, rascjep s Orionom i tajna Polja.",
    canonicalPath: "/what-are-lyra-chronicles",
    canonicalPathEn: "/what-are-lyra-chronicles",
    canonicalPathHr: "/sto-su-kronike-lyre",
    keywordsEn: ["Chronicles of Lyra Prologue", "What Are Lyra Chronicles", "Vega", "Elyra", "Orion", "The Field", "Ancient Archival Fragments", "Pleiades M45"],
    keywordsHr: ["Prolog Kronika Lyre", "Što su zapravo Kronike Lyre", "Vega", "Elyra", "Orion", "Polje", "Drevni arhivski fragmenti", "Plejade M45"],
    ogType: "article",
    schemaType: "Article",
    schemaData: {
      "headline": "What Are the Chronicles of Lyra, Really? (Prologue Codex)",
      "mainEntityOfPage": "https://pleiades-m45.com/what-are-lyra-chronicles"
    }
  },
  lyraLastRecord: {
    titleEn: "Chronicles of Lyra: Prologue – The Last Record • Pleiades M45",
    titleHr: "Kronike Lyre: Prolog – Posljednji Zapis • Plejade M45",
    descriptionEn: "The burning sky over Lyra, the crystal core beneath the Tower of Memory, and the departure of the Seven Arks into the corridors of light. Testimony of the Last Archivist.",
    descriptionHr: "Nebo nad Lyrom koje gori, kristalna jezgra ispod Kule Sjećanja i odlazak Sedam Arki u svjetlosne koridore. Svjedočanstvo Posljednjeg Arhivara.",
    canonicalPath: "/lyra-chronicles/last-record",
    canonicalPathEn: "/lyra-chronicles/last-record",
    canonicalPathHr: "/kronike-lyre/posljednji-zapis",
    keywordsEn: ["Chronicles of Lyra Prologue", "The Last Record", "Last Archivist", "Seven Arks", "Tower of Memory", "Fall of Lyra", "Vega", "Pleiades M45"],
    keywordsHr: ["Kronike Lyre Prolog", "Posljednji Zapis", "Posljednji Arhivar", "Sedam Arki", "Kula Sjećanja", "Pad Lyre", "Vega", "Plejade M45"],
    ogType: "article",
    schemaType: "Article",
    schemaData: {
      "headline": "Chronicles of Lyra: Prologue – The Last Record",
      "mainEntityOfPage": "https://pleiades-m45.com/lyra-chronicles/last-record"
    }
  },
  lyraArchive: {
    titleEn: "The Great Archive of Lyra • High Digital Repository • Pleiades M45",
    titleHr: "Veliki Arhiv Lyre • Visoki Digitalni Trezor • Plejade M45",
    descriptionEn: "Explore the declassified digital archives of Lyra: The Ring Nebula origins, Vega celestial blueprints, and the historical records of the First Empire.",
    descriptionHr: "Istražite deklasificirane digitalne arhive Lyre: podrijetlo iz Maglice Prstena, nebeske nacrte Vege i povijesne zapise Prvog Carstva.",
    canonicalPath: "/lyra-chronicles/archive",
    canonicalPathEn: "/lyra-chronicles/archive",
    canonicalPathHr: "/kronike-lyre/arhiv",
    keywordsEn: ["Great Archive of Lyra", "Veliki Arhiv Lyre", "Digital Archive", "Vega Blueprints", "Ring Nebula", "Pleiades M45"],
    keywordsHr: ["Veliki Arhiv Lyre", "Digitalni arhiv", "Vega", "Maglica Prstena", "Kronike Lyre", "Plejade M45"],
    ogType: "article",
    schemaType: "CollectionPage",
    schemaData: {
      "headline": "The Great Archive of Lyra (Digital Repository)",
      "mainEntityOfPage": "https://pleiades-m45.com/lyra-chronicles/archive"
    }
  },
  dossiers: {
    titleEn: "Galactic Defense Dossiers • The Fall of Lyra • Pleiades M45",
    titleHr: "Dosjei Galaktičke Obrane • Pad Lyre • Plejade M45",
    descriptionEn: "Strategic military intelligence, planetary defense grid logs, and tactical post-mortem analysis of the Lyra-Orion theater.",
    descriptionHr: "Strateški vojni obavještajni zapisi, logovi planetarne obrambene mreže i taktička analiza sukoba Lyre i Oriona.",
    canonicalPath: "/dossiers",
    ogType: "article"
  },
  wallpapers: {
    titleEn: "Sacred Celestial Art & 4K Starseed Wallpapers • Pleiades M45",
    titleHr: "Sveta Nebeska Umjetnost i 4K Starseed Pozadine • Plejade M45",
    descriptionEn: "Ultra-high-definition wallpapers of the Seven Sisters, sacred geometric mandalas, Pleiadian lightships, and celestial temples.",
    descriptionHr: "Pozadine visoke rezolucije sa Sedam Sestara, svetim geometrijskim mandalama, plejadanskim brodovima i nebeskim hramovima.",
    canonicalPath: "/wallpapers",
    ogType: "website"
  },
  glossary: {
    titleEn: "Pleiadian Galactic Glossary & Cosmic Lexicon • Pleiades M45",
    titleHr: "Plejadanski Galaktički Rječnik i Kozmički Leksikon • Plejade M45",
    descriptionEn: "Comprehensive searchable dictionary of Pleiadian, Lyran, and cosmic terms, metaphysical concepts, and historical terminology.",
    descriptionHr: "Sveobuhvatan pretraživi rječnik plejadanskih, lirskih i kozmičkih pojmova, metafizičkih koncepata i povijesnih naziva.",
    canonicalPath: "/glossary",
    ogType: "website"
  },
  seventhShip: {
    titleEn: "ARK-7 The Seventh Ship • Decrypted Stellar Archive • Pleiades M45",
    titleHr: "ARK-7 Sedmi Brod • Dešifrirani Zvjezdani Arhiv • Plejade M45",
    descriptionEn: "Decrypted flight telemetry and archival logs of the Seventh Ship ARK-7, the Ark of Memory.",
    descriptionHr: "Dešifrirana telemetrija leta i arhivski zapisi Sedmog Broda ARK-7, Broda Pamćenja.",
    canonicalPath: "/archive/seventh-ship",
    ogType: "article"
  },
  firstSeparation: {
    titleEn: "The First Separation • Ancient Memory Vault • Pleiades M45",
    titleHr: "Prvo Razdvajanje • Drevni Arhiv Pamćenja • Plejade M45",
    descriptionEn: "Archival record of the First Separation: cosmic divergence, consciousness fragmentation, and ancient stellar memories.",
    descriptionHr: "Arhivski zapis Prvog Razdvajanja: kozmičko razilaženje, fragmentacija svijesti i drevna zvjezdana sjećanja.",
    canonicalPath: "/archive/first-separation",
    ogType: "article"
  },
  signalLost: {
    titleEn: "Signal Lost — Final Transmission • Pleiades M45",
    titleHr: "Izgubljeni Signal — Posljednji Prijenos • Plejade M45",
    descriptionEn: "The fading carrier wave and last known telemetry packet from the outer perimeter.",
    descriptionHr: "Nestajući nosivi val i posljednji poznati telemetrijski paket s vanjskog perimetra.",
    canonicalPath: "/archive/signal-lost",
    ogType: "article"
  }
};

/**
 * Updates DOM title, meta description, OpenGraph, Twitter card, and canonical link
 */
export function updateDocumentSeo(config: PageSeoConfig, isEn: boolean = true) {
  if (typeof document === "undefined") return;

  const title = isEn ? config.titleEn : config.titleHr;
  const description = isEn ? config.descriptionEn : config.descriptionHr;
  const currentPath = isEn
    ? (config.canonicalPathEn || config.canonicalPath)
    : (config.canonicalPathHr || config.canonicalPath);
  const canonicalUrl = `https://pleiades-m45.com${currentPath}`;

  // 1. Title
  document.title = title;

  // 2. Meta description
  let descMeta = document.querySelector('meta[name="description"]');
  if (!descMeta) {
    descMeta = document.createElement("meta");
    descMeta.setAttribute("name", "description");
    document.head.appendChild(descMeta);
  }
  descMeta.setAttribute("content", description);

  // 3. OpenGraph tags
  const setMetaProperty = (property: string, content: string) => {
    let el = document.querySelector(`meta[property="${property}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("property", property);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  setMetaProperty("og:title", title);
  setMetaProperty("og:description", description);
  setMetaProperty("og:url", canonicalUrl);
  setMetaProperty("og:type", config.ogType || "website");
  setMetaProperty("og:site_name", "Pleiades M45 • Celestial Intelligence");
  const ogImgUrl = config.ogImage ? (config.ogImage.startsWith("http") ? config.ogImage : `https://pleiades-m45.com${config.ogImage}`) : "https://pleiades-m45.com/og-image.jpg";
  setMetaProperty("og:image", ogImgUrl);
  setMetaProperty("og:image:width", "1200");
  setMetaProperty("og:image:height", "630");

  // 4. Twitter tags
  const setMetaName = (name: string, content: string) => {
    let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  setMetaName("twitter:title", title);
  setMetaName("twitter:description", description);
  setMetaName("twitter:url", canonicalUrl);
  setMetaName("twitter:card", "summary_large_image");
  setMetaName("twitter:image", ogImgUrl);

  // 5. Canonical Link and Alternate Hreflang
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement("link");
    canonicalLink.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute("href", canonicalUrl);

  // Alternate Hreflang Links for Google Multilingual Indexing
  const setAlternateLink = (hreflang: string, href: string) => {
    let altLink = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
    if (!altLink) {
      altLink = document.createElement("link");
      altLink.setAttribute("rel", "alternate");
      altLink.setAttribute("hreflang", hreflang);
      document.head.appendChild(altLink);
    }
    altLink.setAttribute("href", href);
  };

  const enUrl = `https://pleiades-m45.com${config.canonicalPathEn || config.canonicalPath}`;
  const hrUrl = `https://pleiades-m45.com${config.canonicalPathHr || config.canonicalPath}`;
  setAlternateLink("en", enUrl);
  setAlternateLink("hr", hrUrl);
  setAlternateLink("x-default", enUrl);

  // 6. Dynamic JSON-LD Structured Data
  let ldJsonScript = document.getElementById("dynamic-seo-ldjson") as HTMLScriptElement | null;
  if (!ldJsonScript) {
    ldJsonScript = document.createElement("script");
    ldJsonScript.id = "dynamic-seo-ldjson";
    ldJsonScript.type = "application/ld+json";
    document.head.appendChild(ldJsonScript);
  }

  const isDataset = config.schemaType === "Dataset";

  const structuredData: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": config.schemaType || "WebPage",
    "name": title,
    "description": description,
    "url": canonicalUrl,
    "inLanguage": isEn ? "en" : "hr",
    ...(isDataset
      ? {
          "includedInDataCatalog": {
            "@type": "DataCatalog",
            "name": isEn ? "Pleiadian Galactic Library" : "Plejadanska Galaktička Knjižnica",
            "url": "https://pleiades-m45.com/pleiades-library"
          }
        }
      : {
          "isPartOf": {
            "@type": "WebSite",
            "name": "Pleiades M45 • Celestial Intelligence & Sacred Geometry",
            "url": "https://pleiades-m45.com"
          }
        }),
    ...(config.schemaData || {})
  };

  ldJsonScript.textContent = JSON.stringify(structuredData);
}
